import type { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';

import { useStoreDispatch } from '@hook/store/store.hooks';
import type { BaseStoreInterface } from '@interface/store/base-store.interface';
import { UNLOADED_STATUS } from '@util/constants/api.constants';
import { isExist } from '@util/data/is-exist';

// disable because it has false positives on the generic declaration
/* eslint-disable @typescript-eslint/naming-convention */

interface ReducerActionsInterface {
  startRequest: ActionCreatorWithoutPayload;
  setLoaded: ActionCreatorWithPayload<boolean>;
  endRequest: ActionCreatorWithPayload<Pick<BaseStoreInterface, 'error'>>;
}

// This is to extract the types from the supplied mutation
type ExtractMutationArgType<T> = T extends readonly [(arg: infer A) => any, any] ? A : unknown;
type ExtractMutationDataType<T> = T extends readonly [any, { data?: infer D }] ? D : unknown;
type ExtractMutationStateType<T> = T extends readonly [any, infer S] ? S : unknown;
type ExtractMutationReturnType<T> = T extends readonly [(arg: any) => infer R, any] ? R : unknown;

export const useMutation = <
  TMutation extends readonly [(...args: any[]) => any, any],
  TArg = ExtractMutationArgType<TMutation>,
  TData = ExtractMutationDataType<TMutation>,
  TState = ExtractMutationStateType<TMutation>,
  TReturn = ExtractMutationReturnType<TMutation>,
>(
  mutation: TMutation,
  actions: ReducerActionsInterface,
  setData?: ActionCreatorWithPayload<TData>,
) => {
  const dispatch = useStoreDispatch();
  const [call, state] = mutation;

  useEffect(
    () => void (!UNLOADED_STATUS.has(state.status) && dispatch(actions.endRequest({ error: state.error ?? null }))),
    [state.status],
  );

  useEffect(
    () => void (isExist(state.data) && isExist(setData) && dispatch(setData(state.data))),
    [state.data, state.error, setData],
  );

  useEffect(() => void dispatch(actions.setLoaded(!UNLOADED_STATUS.has(state.status))), [state.status]);

  return {
    action: useCallback(
      (data: TArg): TReturn => {
        dispatch(actions.startRequest());

        return call(data).unwrap();
      },
      [dispatch, call, actions],
    ),
    state: state as TState,
  };
};
